import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './fonts'
import './index.css'
import i18n from './i18n'
import { initSentry } from './lib/sentry'
import { installStaleChunkReload } from './lib/chunkReload'

// Initialize Sentry before rendering the app
initSentry();

// Reload once when a lazy chunk from a previous deploy is gone
installStaleChunkReload();

// Optional react-scan instrumentation — opt-in via `?scan=1` URL flag.
// Dev-only: `import.meta.env.DEV` is statically replaced with `false` in
// production builds so Vite tree-shakes both the conditional and the
// dynamic `import('react-scan')` out of the prod bundle. Must run before
// React mounts to instrument component renders.
async function maybeInitReactScan(): Promise<void> {
  if (!import.meta.env.DEV) return;
  const params = new URLSearchParams(window.location.search);
  if (params.get('scan') !== '1') return;
  try {
    const { scan } = await import('react-scan');
    scan({ enabled: true });
  } catch (err) {
    // Dev-only tooling — don't escalate to Sentry, just surface in the console
    // so the dev who opted in via ?scan=1 sees why it didn't load.
    // eslint-disable-next-line no-console
    console.warn('react-scan failed to load', err);
  }
}

const renderApp = () => {
  createRoot(document.getElementById("root")!).render(
    <App />
  );
};

const bootstrap = async () => {
  await maybeInitReactScan();
  // Wait for i18n to initialize before rendering to ensure translations are loaded
  if (i18n.isInitialized) {
    renderApp();
  } else {
    i18n.on('initialized', renderApp);
  }
};

void bootstrap();
